import React, { createContext, useContext, useState, useEffect } from "react";
import {
    signInWithPopup,
    signOut,
    onAuthStateChanged
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, googleProvider, db } from "../firebase";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isMasterMode, setIsMasterMode] = useState(false);

    // Persistence for Master Mode (Local Bypass)
    useEffect(() => {
        const savedMasterMode = localStorage.getItem("qr_master_mode") === "true";
        if (savedMasterMode) {
            setIsMasterMode(true);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Sync user profile to Firestore
                const userRef = doc(db, "users", user.uid);
                const userDoc = await getDoc(userRef);

                const userData = {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    updatedAt: serverTimestamp()
                };

                if (!userDoc.exists()) {
                    userData.createdAt = serverTimestamp();
                    userData.role = 'coach';
                }

                await setDoc(userRef, userData, { merge: true });
                setCurrentUser({ ...user, ...userDoc.data(), ...userData });
            } else {
                setCurrentUser(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const loginWithGoogle = async () => {
        setLoading(true);
        try {
            await signInWithPopup(auth, googleProvider);
            setIsMasterMode(false);
            localStorage.removeItem("qr_master_mode");
        } catch (error) {
            console.error("Login failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        setLoading(true);
        try {
            await signOut(auth);
            setIsMasterMode(false);
            localStorage.removeItem("qr_master_mode");
        } catch (error) {
            console.error("Logout failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const enterMasterMode = () => {
        setIsMasterMode(true);
        localStorage.setItem("qr_master_mode", "true");
    };

    const value = {
        currentUser,
        loading,
        isMasterMode,
        loginWithGoogle,
        logout,
        enterMasterMode
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
